import { FieldErrors, useForm, UseFormRegister } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import { Maybe, Source as SourceGraphQL, UpdateSourceInput } from '@ui/__generated__/graphql'
import classNames from '@ui/lib/utils/classNames'
import { SaveAndCancelButtons } from '@ui/pages/pipelinesWorkflow/components/DestinationForms/SaveAndCancelButtons'
import * as Yup from 'yup'

type SourceForm = Pick<UpdateSourceInput, 'name'>

type SourceFormProps = {
  source: SourceGraphQL
  onSave: (form: SourceForm) => void
  canEdit: boolean
  onCancel: () => void
}

const NameInput = ({
  canEdit,
  register,
  errors,
}: {
  canEdit: boolean
  register: UseFormRegister<SourceForm>
  errors: FieldErrors<SourceForm>
}) => {
  return (
    <div>
      <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
        Name*
      </label>
      <div className="mt-2">
        <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600 sm:max-w-md">
          <input
            type="text"
            className={classNames(
              canEdit ? 'text-gray-900' : 'text-gray-400',
              'block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
            )}
            placeholder="My Source"
            {...register('name')}
            readOnly={!canEdit}
          />
        </div>
      </div>
      {errors['name'] && <p className="mt-2 text-xs italic text-red-500">{errors['name'].message}</p>}
    </div>
  )
}

const ApiKeyInput = ({ apiKey }: { apiKey?: Maybe<string> }) => {
  const value = apiKey ? `****${apiKey.slice(-4)}` : ''

  return (
    <div>
      <label htmlFor="apiKey" className="block text-sm font-medium leading-6 text-gray-900">
        Api Key*
      </label>
      <div className="mt-2">
        <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600 sm:max-w-md">
          <input
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="abcd1234"
            readOnly // cannot change API keys
            value={value}
          />
        </div>
      </div>
    </div>
  )
}

export const SourceForm = ({ canEdit, source, onSave, onCancel }: SourceFormProps) => {
  const schema = Yup.object({
    name: Yup.string().required('Name is required'),
  }).required()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...source,
    },
  })

  return (
    <form className="flex flex-col gap-6 rounded-b-md bg-white p-10" onSubmit={handleSubmit(onSave)}>
      <NameInput canEdit={canEdit} register={register} errors={errors} />
      <ApiKeyInput apiKey={source.apiKey} />
      {canEdit && <SaveAndCancelButtons cancelClick={onCancel} />}
    </form>
  )
}
